import { useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Loader2, ChevronDown, Plus, Trash2 } from 'lucide-react'
import type { EncuestaDetail, EncuestaCreatePayload } from '../types'

const rangoSchema = z.object({
  min: z.coerce.number(),
  max: z.coerce.number(),
  titulo: z.string().min(1, 'Requerido'),
  descripcion: z.string().optional(),
})

const encuestaSchema = z.object({
  titulo: z.string().min(1, 'El título es requerido'),
  descripcion: z.string().optional(),
  imagen: z.string().optional(),
  campo_principal_activo: z.boolean(),
  campo_principal_etiqueta: z.string().optional(),
  campo_principal_placeholder: z.string().optional(),
  campo_principal_requerido: z.boolean(),
  asociado_laboratorio: z.boolean(),
  laboratorio_id: z.coerce.number().optional().nullable(),
  grupo_laboratorio_id: z.coerce.number().optional().nullable(),
  activa: z.boolean(),
  url_redireccion: z.string().optional(),
  permitir_multiples_respuestas: z.boolean(),
  mostrar_resultados: z.boolean(),
  rangos_resultados: z.array(rangoSchema).optional().nullable(),
  footer: z.string().optional(),
  fecha_inicio: z.string().optional().nullable(),
  fecha_fin: z.string().optional().nullable(),
})

type EncuestaFormData = z.infer<typeof encuestaSchema>

interface EncuestaFormProps {
  onSubmit: (data: EncuestaCreatePayload) => void
  isSubmitting: boolean
  encuesta?: EncuestaDetail | null
  mode: 'create' | 'edit'
}

export function EncuestaForm({ onSubmit, isSubmitting, encuesta, mode }: EncuestaFormProps) {
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control } = useForm<EncuestaFormData>({
    resolver: zodResolver(encuestaSchema),
    defaultValues: {
      titulo: '',
      descripcion: '',
      imagen: '',
      campo_principal_activo: false,
      campo_principal_etiqueta: '',
      campo_principal_placeholder: '',
      campo_principal_requerido: true,
      asociado_laboratorio: false,
      laboratorio_id: null,
      grupo_laboratorio_id: null,
      activa: true,
      url_redireccion: '',
      permitir_multiples_respuestas: false,
      mostrar_resultados: false,
      rangos_resultados: [],
      footer: '',
      fecha_inicio: null,
      fecha_fin: null,
    },
  })

  const { fields: rangos, append: appendRango, remove: removeRango } = useFieldArray({
    control,
    name: 'rangos_resultados',
  })

  const campoPrincipalActivo = watch('campo_principal_activo')
  const mostrarResultados = watch('mostrar_resultados')

  useEffect(() => {
    if (encuesta && mode === 'edit') {
      reset({
        titulo: encuesta.titulo,
        descripcion: encuesta.descripcion || '',
        imagen: encuesta.imagen || '',
        campo_principal_activo: encuesta.campo_principal_activo,
        campo_principal_etiqueta: encuesta.campo_principal_etiqueta || '',
        campo_principal_placeholder: encuesta.campo_principal_placeholder || '',
        campo_principal_requerido: encuesta.campo_principal_requerido,
        asociado_laboratorio: encuesta.asociado_laboratorio,
        laboratorio_id: null,
        grupo_laboratorio_id: encuesta.grupo_laboratorio_id,
        activa: encuesta.activa,
        url_redireccion: encuesta.url_redireccion || '',
        permitir_multiples_respuestas: encuesta.permitir_multiples_respuestas,
        mostrar_resultados: encuesta.mostrar_resultados,
        rangos_resultados: encuesta.rangos_resultados || [],
        footer: encuesta.footer || '',
        fecha_inicio: encuesta.fecha_inicio?.slice(0, 16) || null,
        fecha_fin: encuesta.fecha_fin?.slice(0, 16) || null,
      })
    }
  }, [encuesta, mode, reset])

  const handleFormSubmit = (data: EncuestaFormData) => {
    const payload: EncuestaCreatePayload = {
      ...data,
      descripcion: data.descripcion || '',
      imagen: data.imagen || '',
      fecha_inicio: data.fecha_inicio || null,
      fecha_fin: data.fecha_fin || null,
      rangos_resultados: data.mostrar_resultados
        ? (data.rangos_resultados || []).map(r => ({ ...r, descripcion: r.descripcion || '' }))
        : null,
    }
    onSubmit(payload)
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Información básica */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg">Información básica</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="titulo">Título *</Label>
            <Input id="titulo" {...register('titulo')} placeholder="Título de la encuesta" />
            {errors.titulo && <p className="text-sm text-destructive">{errors.titulo.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea id="descripcion" {...register('descripcion')} rows={3} placeholder="Descripción de la encuesta" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="imagen">URL de imagen</Label>
            <Input id="imagen" {...register('imagen')} placeholder="https://..." />
          </div>
        </CardContent>
      </Card>

      {/* Campo principal */}
      <Collapsible>
        <Card className="border border-gray-300 dark:border-gray-700">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50">
              <CardTitle className="text-lg flex items-center justify-between">
                Campo principal
                <ChevronDown className="h-4 w-4" />
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="campo_principal_activo">Mostrar campo principal</Label>
                <Switch
                  id="campo_principal_activo"
                  checked={campoPrincipalActivo}
                  onCheckedChange={(v) => setValue('campo_principal_activo', v)}
                  className="data-[state=checked]:bg-teal-500"
                />
              </div>
              {campoPrincipalActivo && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="campo_principal_etiqueta">Etiqueta</Label>
                    <Input id="campo_principal_etiqueta" {...register('campo_principal_etiqueta')} placeholder="Ej: Nombre del doctor" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="campo_principal_placeholder">Placeholder</Label>
                    <Input id="campo_principal_placeholder" {...register('campo_principal_placeholder')} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="campo_principal_requerido">Requerido</Label>
                    <Switch
                      id="campo_principal_requerido"
                      checked={watch('campo_principal_requerido')}
                      onCheckedChange={(v) => setValue('campo_principal_requerido', v)}
                      className="data-[state=checked]:bg-teal-500"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Configuración */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg">Configuración</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="activa">Activa</Label>
            <Switch id="activa" checked={watch('activa')} onCheckedChange={(v) => setValue('activa', v)} className="data-[state=checked]:bg-teal-500" />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="permitir_multiples_respuestas">Permitir múltiples respuestas por IP</Label>
            <Switch
              id="permitir_multiples_respuestas"
              checked={watch('permitir_multiples_respuestas')}
              onCheckedChange={(v) => setValue('permitir_multiples_respuestas', v)}
              className="data-[state=checked]:bg-teal-500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="url_redireccion">URL de redirección (tras completar)</Label>
            <Input id="url_redireccion" {...register('url_redireccion')} placeholder="https://..." />
          </div>
        </CardContent>
      </Card>

      {/* Resultados */}
      <Collapsible>
        <Card className="border border-gray-300 dark:border-gray-700">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50">
              <CardTitle className="text-lg flex items-center justify-between">
                Resultados calculados
                <ChevronDown className="h-4 w-4" />
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="mostrar_resultados">Mostrar resultados calculados</Label>
                <Switch
                  id="mostrar_resultados"
                  checked={mostrarResultados}
                  onCheckedChange={(v) => setValue('mostrar_resultados', v)}
                  className="data-[state=checked]:bg-teal-500"
                />
              </div>
              {mostrarResultados && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">Define rangos de puntaje y su resultado correspondiente.</p>
                  {rangos.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-[1fr_1fr_2fr_2fr_auto] gap-2 items-start">
                      <div>
                        <Label className="text-xs">Min</Label>
                        <Input type="number" {...register(`rangos_resultados.${index}.min`)} />
                      </div>
                      <div>
                        <Label className="text-xs">Max</Label>
                        <Input type="number" {...register(`rangos_resultados.${index}.max`)} />
                      </div>
                      <div>
                        <Label className="text-xs">Título</Label>
                        <Input {...register(`rangos_resultados.${index}.titulo`)} />
                      </div>
                      <div>
                        <Label className="text-xs">Descripción</Label>
                        <Input {...register(`rangos_resultados.${index}.descripcion`)} />
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="mt-5" onClick={() => removeRango(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendRango({ min: 0, max: 0, titulo: '', descripcion: '' })}
                  >
                    <Plus className="mr-2 h-4 w-4" /> Agregar rango
                  </Button>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Vigencia */}
      <Collapsible>
        <Card className="border border-gray-300 dark:border-gray-700">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50">
              <CardTitle className="text-lg flex items-center justify-between">
                Vigencia y footer
                <ChevronDown className="h-4 w-4" />
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fecha_inicio">Fecha inicio</Label>
                  <Input id="fecha_inicio" type="datetime-local" {...register('fecha_inicio')} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fecha_fin">Fecha fin</Label>
                  <Input id="fecha_fin" type="datetime-local" {...register('fecha_fin')} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="footer">Footer</Label>
                <Textarea id="footer" {...register('footer')} rows={2} placeholder="Texto del pie de página" />
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Submit */}
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isSubmitting} className="bg-teal-800 hover:bg-teal-900 text-white">
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {mode === 'create' ? 'Crear encuesta' : 'Guardar cambios'}
        </Button>
      </div>
    </form>
  )
}
